import { useState, useEffect } from 'react'
import { Users, Store, Package, ShoppingCart, BarChart3, DollarSign, Activity, Shield, RefreshCw, TrendingUp, TrendingDown } from 'lucide-react'
import StatCard from '@components/StatCard'
import RecentActivity from '@components/RecentActivity'
import SalesChart from '@components/SalesChart'
import LiveOrdersWidget from '@components/LiveOrdersWidget'
import { adminService } from '@services/adminService'
import { merchantService } from '@services/merchantService'
import { productService } from '@services/productService'
import { userService } from '@services/userService'
import { reservationService } from '@services/reservationService'

interface RevenueSummary {
  total_revenue: number
  monthly_revenue: number
  revenue_growth: number
}

interface UserStats {
  total_users: number
  active_users: number
  new_users_today: number
}

interface SystemHealth {
  status: 'healthy' | 'degraded' | 'down'
  services: { name: string; status: 'up' | 'down'; latency: number }[]
}

const DashboardPage = () => {
  const [stats, setStats] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [revenueSummary, setRevenueSummary] = useState<RevenueSummary | null>(null)
  const [userStats, setUserStats] = useState<UserStats | null>(null)
  const [adminActivity, setAdminActivity] = useState<any[]>([])
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const fetchDashboardStats = async () => {
    try {
      setLoading(true)
      
      // Try to fetch from dashboard API first
      let dashboardData = null
      try {
        const response = await adminService.getDashboardStats()
        if (response.success && response.data) {
          dashboardData = response.data
        }
      } catch (dashboardError) {
        console.log('Dashboard API failed, trying individual APIs...')
      }
      
      // If dashboard API failed, try individual APIs
      if (!dashboardData) {
        const extractTotal = (response: any): number => {
          if (!response) return 0
          if (response.pagination && typeof response.pagination.total === 'number') {
            return response.pagination.total
          }
          if (typeof response.total === 'number') {
            return response.total
          }
          if (response.success && typeof response.total === 'number') {
            return response.total
          }
          return response.total || response.pagination?.total || 0
        }
        
        const safeFetch = async (fetchFn: () => Promise<any>, defaultValue: number = 0, label: string = 'API'): Promise<number> => {
          try {
            const response = await fetchFn()
            const total = extractTotal(response)
            return total
          } catch (error) {
            console.warn(`${label} fetch failed, using default:`, error)
            return defaultValue
          }
        }
        
        const [
          totalMerchants,
          totalProducts,
          totalReservations,
          totalUsers
        ] = await Promise.all([
          safeFetch(() => merchantService.getAll({ limit: 1 }), 0, 'Merchants API'),
          safeFetch(() => productService.getAll({ limit: 1 }), 0, 'Products API'),
          safeFetch(() => reservationService.getAll({}), 0, 'Reservations API'),
          (async () => {
            try {
              const usersStats = await userService.getStatistics()
              return usersStats.active || usersStats.total || 0
            } catch (error) {
              console.warn('Users stats API failed, trying getAll:', error)
              try {
                const usersResponse = await userService.getAll({})
                return extractTotal(usersResponse)
              } catch (fallbackError) {
                console.warn('Users getAll also failed, using 0:', fallbackError)
                return 0
              }
            }
          })()
        ])
        
        dashboardData = {
          total_merchants: totalMerchants,
          total_products: totalProducts,
          total_reservations: totalReservations,
          total_users: totalUsers,
          total_revenue: 0,
          recent_activity: []
        }
      }
      
      const formatNumber = (num: number) => {
        if (typeof num !== 'number' || isNaN(num)) {
          return '0'
        }
        return num.toLocaleString('fr-FR')
      }
      
      const transformedStats = [
        {
          title: 'Commerçants totaux',
          value: formatNumber(dashboardData.total_merchants),
          icon: Store,
          color: 'primary' as const,
        },
        {
          title: 'Produits totaux',
          value: formatNumber(dashboardData.total_products),
          icon: Package,
          color: 'success' as const,
        },
        {
          title: 'Réservations actives',
          value: formatNumber(dashboardData.total_reservations),
          icon: ShoppingCart,
          color: 'warning' as const,
        },
        {
          title: 'Utilisateurs actifs',
          value: formatNumber(dashboardData.total_users),
          icon: Users,
          color: 'danger' as const,
        },
      ]
      
      setStats(transformedStats)
      setError(null)

      // Fetch revenue summary
      try {
        const revenueRes = await adminService.getRevenueData('monthly')
        if (revenueRes.success && revenueRes.data.length > 0) {
          const data = revenueRes.data
          const totalRevenue = data.reduce((sum, d) => sum + d.amount, 0)
          const monthlyRevenue = data[data.length - 1]?.amount || 0
          const prevMonthRevenue = data.length > 1 ? data[data.length - 2]?.amount || 0 : 0
          const growth = prevMonthRevenue > 0 ? ((monthlyRevenue - prevMonthRevenue) / prevMonthRevenue) * 100 : 0
          setRevenueSummary({ total_revenue: totalRevenue, monthly_revenue: monthlyRevenue, revenue_growth: growth })
        }
      } catch (e) {
        console.log('Revenue data fetch failed')
      }

      // Fetch user stats
      try {
        const growthRes = await adminService.getUserGrowth('daily')
        if (growthRes.success && growthRes.data.length > 0) {
          const data = growthRes.data
          const totalUsers = data.reduce((sum, d) => sum + d.registrations, 0)
          const activeUsers = Math.round(totalUsers * 0.7)
          const newToday = data[data.length - 1]?.registrations || 0
          setUserStats({ total_users: totalUsers, active_users: activeUsers, new_users_today: newToday })
        }
      } catch (e) {
        console.log('User growth fetch failed')
      }

      // Fetch admin activity
      try {
        const activityRes = await adminService.getAdminActivity(10, 0)
        if (activityRes.success) {
          setAdminActivity(activityRes.data)
        }
      } catch (e) {
        console.log('Admin activity fetch failed')
      }

      // Fetch system health
      try {
        const healthRes = await adminService.getSystemHealth()
        const services = Object.entries(healthRes.services).map(([name, info]) => ({
          name,
          status: info.status === 'OK' ? 'up' as const : 'down' as const,
          latency: 0
        }))
        const allUp = services.every(s => s.status === 'up')
        setSystemHealth({
          status: allUp ? 'healthy' : 'degraded',
          services
        })
      } catch (e) {
        console.log('System health fetch failed')
      }

    } catch (err) {
      console.error('Error fetching dashboard stats:', err)
      setError('Impossible de récupérer les données en temps réel. Les statistiques ne sont pas disponibles.')
      setStats([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboardStats()
  }, [])

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchDashboardStats()
    setRefreshing(false)
  }

  const getHealthColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-50 border-green-200'
      case 'degraded': return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      case 'down': return 'text-red-600 bg-red-50 border-red-200'
      default: return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  const getServiceStatusColor = (status: string) => {
    switch (status) {
      case 'up': return 'text-green-600'
      case 'down': return 'text-red-600'
      default: return 'text-gray-600'
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tableau de bord</h1>
            <p className="text-gray-600">Bienvenue dans l'administration Walomaydi3</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                    <div className="h-8 bg-gray-200 rounded w-16"></div>
                  </div>
                  <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="card animate-pulse">
              <div className="card-header">
                <div className="h-5 bg-gray-200 rounded w-48"></div>
              </div>
              <div className="card-body">
                <div className="h-64 bg-gray-100 rounded"></div>
              </div>
            </div>
          </div>
          <div>
            <div className="card animate-pulse">
              <div className="card-header">
                <div className="h-5 bg-gray-200 rounded w-32"></div>
              </div>
              <div className="card-body p-0">
                <div className="h-64 bg-gray-100 rounded"></div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card animate-pulse">
            <div className="card-header">
              <div className="h-5 bg-gray-200 rounded w-40"></div>
            </div>
            <div className="card-body">
              <div className="h-32 bg-gray-100 rounded"></div>
            </div>
          </div>
          <div className="card animate-pulse">
            <div className="card-header">
              <div className="h-5 bg-gray-200 rounded w-36"></div>
            </div>
            <div className="card-body">
              <div className="h-32 bg-gray-100 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tableau de bord</h1>
            <p className="text-gray-600">Bienvenue dans l'administration Walomaydi3</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Réessayer</span>
          </button>
        </div>
        
        <div className="card border-red-200 bg-red-50">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erreur de chargement</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tableau de bord</h1>
          <p className="text-gray-600">Bienvenue dans l'administration Walomaydi3</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Actualiser</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      {/* Charts and Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales Chart */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Ventes - 30 derniers jours</h2>
            </div>
            <div className="card-body">
              <SalesChart />
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Activité récente</h2>
            </div>
            <div className="card-body p-0">
              <RecentActivity />
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Summary & User Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Summary Widget */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <DollarSign className="h-5 w-5 text-green-500 mr-2" />
                Résumé des revenus
              </h2>
            </div>
          </div>
          <div className="card-body">
            {revenueSummary ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                  <p className="text-sm text-green-600 font-medium">Revenus totaux</p>
                  <p className="text-2xl font-bold text-green-800 mt-1">
                    ${revenueSummary.total_revenue.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-sm text-blue-600 font-medium">Revenus mensuels</p>
                  <p className="text-2xl font-bold text-blue-800 mt-1">
                    ${revenueSummary.monthly_revenue.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="col-span-2 p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600 font-medium">Taux de croissance</p>
                    <div className={`flex items-center ${revenueSummary.revenue_growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {revenueSummary.revenue_growth >= 0 ? (
                        <TrendingUp className="h-4 w-4 mr-1" />
                      ) : (
                        <TrendingDown className="h-4 w-4 mr-1" />
                      )}
                      <span className="font-semibold">{Math.abs(revenueSummary.revenue_growth).toFixed(1)}%</span>
                    </div>
                  </div>
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${revenueSummary.revenue_growth >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                      style={{ width: `${Math.min(Math.abs(revenueSummary.revenue_growth), 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <DollarSign className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>Données de revenus non disponibles</p>
              </div>
            )}
          </div>
        </div>

        {/* User Stats Widget */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Users className="h-5 w-5 text-primary-500 mr-2" />
                Statistiques des utilisateurs
              </h2>
            </div>
          </div>
          <div className="card-body">
            {userStats ? (
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-primary-50 rounded-lg border border-primary-100 text-center">
                  <p className="text-sm text-primary-600 font-medium">Utilisateurs totaux</p>
                  <p className="text-2xl font-bold text-primary-800 mt-1">{userStats.total_users.toLocaleString('fr-FR')}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-100 text-center">
                  <p className="text-sm text-green-600 font-medium">Utilisateurs actifs</p>
                  <p className="text-2xl font-bold text-green-800 mt-1">{userStats.active_users.toLocaleString('fr-FR')}</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100 text-center">
                  <p className="text-sm text-blue-600 font-medium">Nouveaux aujourd'hui</p>
                  <p className="text-2xl font-bold text-blue-800 mt-1">{userStats.new_users_today.toLocaleString('fr-FR')}</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>Statistiques des utilisateurs non disponibles</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Live Orders & Admin Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Live Orders Widget */}
        <LiveOrdersWidget />

        {/* Admin Activity */}
        {/* Recent Admin Activity Widget */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Activity className="h-5 w-5 text-purple-500 mr-2" />
                Activité administrative récente
              </h2>
            </div>
          </div>
          <div className="card-body p-0">
            {adminActivity.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {adminActivity.slice(0, 10).map((activity, index) => (
                  <div key={activity.id || index} className="flex items-start px-6 py-3 hover:bg-gray-50">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className={`h-2 w-2 rounded-full ${
                        activity.action_type === 'create' ? 'bg-green-400' :
                        activity.action_type === 'update' ? 'bg-blue-400' :
                        activity.action_type === 'delete' ? 'bg-red-400' :
                        'bg-gray-400'
                      }`} />
                    </div>
                    <div className="ml-3 flex-1 min-w-0">
                      <p className="text-sm text-gray-900 truncate">
                        {activity.admin_name || 'Admin'} - {activity.action_type || 'action'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {typeof activity.details === 'object' && activity.details !== null
                          ? Object.values(activity.details).join(', ') || JSON.stringify(activity.details)
                          : activity.details || activity.resource_type || ''}
                        {activity.resource_id ? ` #${activity.resource_id}` : ''}
                      </p>
                    </div>
                    <div className="ml-3 text-xs text-gray-400 whitespace-nowrap">
                      {activity.created_at ? new Date(activity.created_at).toLocaleDateString('fr-FR', {
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                      }) : ''}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Activity className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>Aucune activité récente</p>
              </div>
            )}
          </div>
        </div>

        {/* System Health Widget */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Shield className="h-5 w-5 text-gray-500 mr-2" />
                État du système
              </h2>
            </div>
          </div>
          <div className="card-body">
            {systemHealth ? (
              <div>
                <div className={`mb-4 p-3 rounded-lg border ${getHealthColor(systemHealth.status)}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">État global</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      systemHealth.status === 'healthy' ? 'bg-green-100 text-green-800' :
                      systemHealth.status === 'degraded' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {systemHealth.status.charAt(0).toUpperCase() + systemHealth.status.slice(1)}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  {systemHealth.services.map((service, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <div className={`h-2 w-2 rounded-full mr-2 ${
                          service.status === 'up' ? 'bg-green-400' : 'bg-red-400'
                        }`} />
                        <span className="text-sm text-gray-700">{service.name}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`text-xs font-medium ${getServiceStatusColor(service.status)}`}>
                          {service.status === 'up' ? 'Opérationnel' : 'Indisponible'}
                        </span>
                        <span className="text-xs text-gray-400">{service.latency}ms</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Shield className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>Données d'état du système non disponibles</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Actions rapides</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="flex flex-col items-center justify-center p-6 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <Store className="h-8 w-8 text-primary-600 mb-2" />
              <span className="font-medium text-gray-900">Ajouter un marchand</span>
              <span className="text-sm text-gray-500">Créer un nouveau compte</span>
            </button>
            <button className="flex flex-col items-center justify-center p-6 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <Package className="h-8 w-8 text-success-600 mb-2" />
              <span className="font-medium text-gray-900">Gérer les produits</span>
              <span className="text-sm text-gray-500">Ajouter/modifier des produits</span>
            </button>
            <button className="flex flex-col items-center justify-center p-6 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <BarChart3 className="h-8 w-8 text-warning-600 mb-2" />
              <span className="font-medium text-gray-900">Voir les rapports</span>
              <span className="text-sm text-gray-500">Analyses détaillées</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage
